import React, { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Typography } from '@mic3/platform-ui';
import { gql, NetworkStatus, useLazyQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';

import Widget from 'app/components/atoms/Widget/Widget';
import LinkList from 'app/containers/LandingPage/Widgets/LinkList';
import { get } from 'app/utils/lo/lo';
import ButtonIcon from 'app/components/molecules/ButtonIcon/ButtonIcon';
import { saveComponentState } from 'store/actions/component/componentActions';
import history from 'store/History';
import { canViewWidget, getRouteLink, getViewId, PriorityTypography, renderDueDate, widgetTypes } from '../dashboardUtils';
import { PROCESSLAYERS, TASKLAYERS } from 'app/utils/maps/layer/layerUtils';
import { formatOptionsForTask, normalizeFilterOptions } from 'app/utils/formatter/graphql-formatter';
import { tasksFields } from 'graphql/abox/task/tasksEntityQuery';
import { getPriorityLabel } from 'app/config/aboxConfig';

const commonFields = `
        createdDate
        modifiedDate
        createdBy {
            name
        }
        modifiedBy {
            name
        }
`;

const LOAD_LIST_TASKS_WIDGET_QUERY = gql`
    query listWidgetTasksQuery($startIndex: Int, $stopIndex: Int, $filterBy: [JSON], $orderBy: [JSON], $excludeBy: [JSON]) {
        records: tasks(startIndex: $startIndex, stopIndex: $stopIndex, filterBy: $filterBy, orderBy: $orderBy, excludeBy: $excludeBy) {
            ${tasksFields}
            process {
                processDefinition {
                    name
                }
            }
            assignee {
                name
            }
            ${commonFields}
        }
    }
`;

const LOAD_LIST_PRCOCESS_WIDGET_QUERY = gql`
    query listWidgetProcessesQuery($startIndex: Int, $stopIndex: Int, $filterBy: [JSON], $orderBy: [JSON], $excludeBy: [JSON]) {
        records: processes(startIndex: $startIndex, stopIndex: $stopIndex, filterBy: $filterBy, orderBy: $orderBy, excludeBy: $excludeBy) {
            id
            name
            type
            primary(fields:["closedDate", "variables", "priority", "sequenceNumber"])
            processDefinition {
                name
            }
            ${commonFields}
        }
    }
`;

// For index attributes, we need data from the primary object.
// Instead of fetching the entire primary object, we only fetch the specific field used for sorting.
const _getEntityQuery = (orderField) => {
    const primaryFields = [];
    if (orderField?.startsWith('primary.')) {
        const subField = orderField.slice('primary.'.length);
        primaryFields.push(`"${subField}"`);
    }
    const primaryFragment = primaryFields.length ? `primary(fields: [${primaryFields.join(', ')}])` : '';
    return gql`
        query listWidgetEntitiesQuery($startIndex: Int, $stopIndex: Int, $filterBy: [JSON], $orderBy: [JSON], $type: String!, $excludeBy: [JSON]) {
            records: entities(startIndex: $startIndex, stopIndex: $stopIndex, filterBy: $filterBy, orderBy: $orderBy, type: $type, excludeBy: $excludeBy) {
                id
                name
                type
                image
                active
                ${commonFields}
                ${primaryFragment}
            }
        }
    `;
};

export const getWidgetQuery = (entityType, orderField) => {
    if (!entityType) return '';
    switch (true) {
        case TASKLAYERS.includes(entityType):
            return LOAD_LIST_TASKS_WIDGET_QUERY;
        case PROCESSLAYERS.includes(entityType):
            return LOAD_LIST_PRCOCESS_WIDGET_QUERY;
        default:
            return _getEntityQuery(orderField);
    }
};

const Flex = styled.div`
    display: flex;
    flex-direction: row;
    width: fit-content;
    cursor: pointer;
    color: ${({ theme }) => theme.material.colors.link};
    &:focus {
        outline: 2px solid ${({ theme }) => theme.material.colors.link};
    }
`;

const ListWidget = (props) => {
    const { item, menuItemClick, permissions, type } = props;
    const { header, subHeader, entityType, filters, filterBy, order, operatorsMap, total } = item || {};
    const userProfile = useSelector((state) => state.user.profile);
    const dispatch = useDispatch();
    const viewId = getViewId(entityType);

    const options = useMemo(() => {
        const isTaskList = TASKLAYERS.includes(entityType);

        let options = {
            orderBy: [order].filter(Boolean),
            filterBy,
            type: entityType,
            startIndex: 0,
            stopIndex: (total || 10) - 1,
        };
        if (isTaskList) {
            options = formatOptionsForTask(options, userProfile);
        } else {
            options = normalizeFilterOptions(options);
        }
        return options;
    }, [entityType, filterBy, userProfile, order, total]);

    const filterViewState = useSelector((state) => state.component?.state?.[viewId] || {});
    const canView = canViewWidget(item, type);
    const query = getWidgetQuery(entityType, order?.field);
    const [fetchWidgetData, { loading, data, networkStatus, refetch }] = useLazyQuery(query, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
    });
    useEffect(() => {
        canView && fetchWidgetData({ variables: options });
    }, [canView, fetchWidgetData, options]);
    const isRefetching = networkStatus === NetworkStatus.refetch;

    const records = React.useMemo(() => data?.records || [], [data]);

    const goToClick = async () => {
        dispatch(
            saveComponentState(viewId, {
                ...filterViewState,
                filters,
                isPreferenceLoaded: true,
                orderBy: order || {},
                operatorsMap,
            })
        ).then(() => {
            const link = getRouteLink(entityType);
            link && history.push(link);
        });
    };

    const renderFooterContent = () => {
        return (
            <Flex onClick={goToClick} role='button' tabIndex={0}>
                <ButtonIcon icon='arrow-right' size='sm' />
                VIEW ALL
            </Flex>
        );
    };

    const renderDate = useCallback((date, orderField) => {
        if (orderField === 'primary.dueDate') {
            const record = { data: { primary: { dueDate: date } } };
            return renderDueDate(record);
        }
        let relativeTime;
        if (date) {
            relativeTime = moment(date).fromNow();
        }
        return <Typography value={0}>{relativeTime || ''}</Typography>;
    }, []);

    const renderAdditionalInfo = useCallback(
        (props) => {
            const orderField = order?.field;
            if (orderField?.includes('priority')) {
                const primary = get(props, 'primary');
                const { priority } = primary;
                const label = getPriorityLabel(priority);
                return <PriorityTypography value={priority}>{label}</PriorityTypography>;
            }
            const fieldData = get(props, orderField);
            if (orderField?.toLowerCase()?.includes('date')) {
                // createdDate, modifiedDate, dueDate, closedDate, startDate
                return renderDate(fieldData, orderField);
            }
            if (orderField?.includes('assignee')) {
                const assigneeName = get(props, 'assignee.name');
                return <Typography>{assigneeName || ''}</Typography>;
            }
            if (orderField?.includes('modifiedBy') || orderField?.includes('createdBy')) {
                const name = get(props, `${orderField}.name`);
                return <Typography>{name || ''}</Typography>;
            }
            return <Typography>{fieldData || ''}</Typography>;
        },
        [order, renderDate]
    );

    const handleMenuItemClick = (menu) => {
        if (menu === 'Refresh') {
            refetch();
        } else {
            menuItemClick && menuItemClick(menu);
        }
    };

    return (
        <Widget
            title={header}
            subHeader={subHeader}
            type={widgetTypes.list}
            url={getRouteLink(entityType)}
            item={item}
            content={
                (!loading && !records.length) || !entityType ? (
                    'No Results'
                ) : (
                    <LinkList list={records} type={entityType} renderAdditionalInfo={renderAdditionalInfo} />
                )
            }
            loading={loading || isRefetching}
            menuItemClick={handleMenuItemClick}
            footerContent={renderFooterContent}
            permissions={permissions}
        />
    );
};

export default ListWidget;
